import { isEmpty } from 'lodash'
import type { OpenAPIV3_1 as OpenApi3 } from 'openapi-types'

import { isNonNullable } from '@subaio/utils'

const alreadyTraversedRefs: Array<{
	name: string
	definition: OpenApi3.SchemaObject
}> = []

type GetRelevantReferenceProps = {
	definitions: Record<string, OpenApi3.SchemaObject | OpenApi3.ReferenceObject> | undefined
	ref: string
	nestingCount?: number
	hideDeprecatedProperties: boolean
}

export const resolveListOfDefinitions = ({
	items,
	definitions,
	nestingCount,
	hideDeprecatedProperties,
}: {
	items: Array<OpenApi3.SchemaObject | OpenApi3.ReferenceObject>
	definitions: Record<string, OpenApi3.SchemaObject | OpenApi3.ReferenceObject> | undefined
	nestingCount?: number
	hideDeprecatedProperties: boolean
}) =>
	items
		.map((item): Record<string, any> | undefined => {
			if ('$ref' in item)
				return getRelevantReference({
					definitions,
					ref: item.$ref,
					hideDeprecatedProperties,
				})
			return getDeepReferences({
				definitions,
				definition: item,
				nestingCount,
				hideDeprecatedProperties,
			})
		})
		.filter(isNonNullable)

const getRelevantReference = ({
	definitions,
	ref,
	nestingCount = 0,
	hideDeprecatedProperties,
}: GetRelevantReferenceProps) => {
	const [nestedRef] = ref.split('/').reverse()
	const nestedDefinition = definitions?.[nestedRef]
	const alreadyTraversedRef = alreadyTraversedRefs.find(({ name }) => name === nestedRef)
	if (alreadyTraversedRef) {
		return getPropertyValue({
			definitions,
			schema: { ...alreadyTraversedRef.definition },
			nestingCount: nestingCount + 1,
			hideDeprecatedProperties,
		})
	}
	if (nestedDefinition && 'type' in nestedDefinition && nestedDefinition.type === 'object') {
		alreadyTraversedRefs.push({
			name: nestedRef,
			definition: nestedDefinition,
		})
	}

	if (nestedDefinition && 'oneOf' in nestedDefinition && nestedDefinition.oneOf) {
		const validOneOfExamples = resolveListOfDefinitions({
			items: nestedDefinition.oneOf,
			definitions,
			nestingCount,
			hideDeprecatedProperties,
		}).filter((item) => !isEmpty(item))
		if (validOneOfExamples.length === 0 && 'type' in nestedDefinition.oneOf[0]) {
			if (nestedDefinition.oneOf[0].type === 'null') return null
			return nestedDefinition.oneOf[0].type
		}
		return validOneOfExamples[0]
	}
	if (nestedDefinition && 'allOf' in nestedDefinition && nestedDefinition.allOf) {
		return resolveListOfDefinitions({
			items: nestedDefinition.allOf,
			definitions,
			nestingCount,
			hideDeprecatedProperties,
		}).reduce((a, b) => ({ ...a, ...b }))
	}
	if (nestedDefinition && 'properties' in nestedDefinition) {
		return getDeepReferences({
			definitions,
			definition: nestedDefinition,
			nestingCount: nestingCount + 1,
			hideDeprecatedProperties,
		})
	}
	if (nestedDefinition && 'enum' in nestedDefinition) {
		return nestedDefinition.enum?.[0]
	}
	if (nestedDefinition && 'type' in nestedDefinition) {
		if (nestedDefinition.example) return nestedDefinition.example
		return nestedDefinition.type
	}
}

const defaultStringExamples: Record<string, string> = {
	// Generic
	uuid: '97e4649d-d457-4853-8ec0-fe4a495e4d82',
	date: '2017-07-21',
	'date-time': '2017-07-21T17:32:28Z',
	email: 'email@example.com',
	uri: 'https://www.example.com',
	// Custom subaio
	brand: 'somebank_dk',
	currency: 'EUR',
	integer: '123',
	double: '123.45',
}

type GetPropertyValueProps = {
	definitions: Record<string, OpenApi3.SchemaObject | OpenApi3.ReferenceObject> | undefined
	schema: OpenApi3.SchemaObject | OpenApi3.ParameterObject | OpenApi3.HeaderObject | OpenApi3.ReferenceObject
	nestingCount?: number
	hideDeprecatedProperties: boolean
}

export const getPropertyValue = ({
	definitions,
	schema,
	nestingCount = 0,
	hideDeprecatedProperties,
}: GetPropertyValueProps): Record<string, any> | string | Array<any> | number | boolean | null | undefined => {
	if ('deprecated' in schema && schema.deprecated && hideDeprecatedProperties) return undefined

	if ('$ref' in schema) {
		return getRelevantReference({
			definitions,
			ref: schema.$ref!,
			nestingCount,
			hideDeprecatedProperties,
		})
	}

	// As long as we don't recurse all the way to hell, we can safely return a ref object, otherwise return the type or ref name values
	if (nestingCount >= 3) {
		if (
			'type' in schema &&
			((schema.type === 'object' &&
				schema.properties &&
				Object.values(schema.properties).some((property: any) => '$ref' in property)) ||
				(schema.type === 'array' && schema.items && Object.keys(schema.items).some((key) => key === '$ref')))
		) {
			return '...'
		}
	}

	if ('example' in schema && schema.example !== undefined) return schema.example

	if ('items' in schema && schema.items) {
		const { items } = schema

		if ('$ref' in items)
			return [
				getRelevantReference({
					definitions,
					ref: items.$ref!,
					nestingCount,
					hideDeprecatedProperties,
				}),
			]
		if ('enum' in items && items.enum) return items.enum
		if ('properties' in items && items.properties)
			return [
				getDeepReferences({
					definitions,
					definition: items as OpenApi3.ReferenceObject,
					nestingCount,
					hideDeprecatedProperties,
				}),
			]
		if ('oneOf' in items && items.oneOf)
			return [
				resolveListOfDefinitions({
					items: items.oneOf,
					definitions,
					nestingCount,
					hideDeprecatedProperties,
				})[0],
			]
		if ('allOf' in items && items.allOf) {
			const resolvedListOfDefinitions = resolveListOfDefinitions({
				items: items.allOf,
				definitions,
				nestingCount,
				hideDeprecatedProperties,
			})
			if (resolvedListOfDefinitions.length === 0) return undefined
			return [resolvedListOfDefinitions.reduce((a, b) => ({ ...a, ...b }))]
		}
		if ('type' in items && items.type) {
			return Array.from({
				length: items.example !== undefined ? 1 : Math.min(items.minItems ?? 3, items.maxItems ?? 3),
			}).map(() => {
				if (items.example !== undefined) return items.example
				if (items.format && items.type?.includes('string')) return defaultStringExamples[items.format] ?? 'string'
				return items.type
			})
		}
	}

	if ('type' in schema && schema.type) {
		const { type: initialType, enum: enums, properties, additionalProperties, format } = schema
		const type = Array.isArray(initialType) ? initialType.flat() : [initialType]
		if (enums) return enums[0]
		if (type.includes('string')) return defaultStringExamples[format!] ?? 'string'
		if (type.includes('integer')) return 123
		if (type.includes('number')) return 123.45
		if (type.includes('boolean')) return true
		if (type.includes('object') && additionalProperties) return additionalProperties
		if (type.includes('object') && properties)
			return getDeepReferences({
				definitions,
				definition: schema,
				nestingCount,
				hideDeprecatedProperties,
			})
		if (type.includes('object')) return {}
		if (type.includes('null')) return null

		throw new Error(`Failed to parse type ${type} in schema`)
	}

	if ('oneOf' in schema && schema.oneOf)
		return resolveListOfDefinitions({
			items: schema.oneOf,
			definitions,
			nestingCount,
			hideDeprecatedProperties,
		})[0]
	if ('allOf' in schema && schema.allOf) {
		const resolvedListOfDefinitions = resolveListOfDefinitions({
			items: schema.allOf,
			definitions,
			nestingCount,
			hideDeprecatedProperties,
		})
		if (resolvedListOfDefinitions.length === 0) return undefined
		return resolvedListOfDefinitions.reduce((a, b) => ({ ...a, ...b }))
	}

	console.error('Could not parse property', schema)
	throw new Error('Failed to parse property')
}

type Props = {
	definitions: Record<string, OpenApi3.SchemaObject | OpenApi3.ReferenceObject> | undefined
	definition: OpenApi3.SchemaObject | OpenApi3.ParameterObject | OpenApi3.ReferenceObject
	nestingCount?: number
	hideDeprecatedProperties: boolean
}

export const getDeepReferences = ({
	definitions,
	definition,
	nestingCount = 0,
	hideDeprecatedProperties,
}: Props): Record<string, any> | undefined => {
	if ('properties' in definition) {
		return Object.fromEntries(
			Object.entries(definition.properties ?? {}).map(([key, schema]) => [
				key,
				getPropertyValue({
					definitions,
					schema: schema as OpenApi3.SchemaObject | OpenApi3.ParameterObject,
					nestingCount,
					hideDeprecatedProperties,
				}),
			]),
		)
	}

	return undefined
}
